import { useState, useEffect, useRef, useCallback } from 'react';
import Navigation from '../components/Navigation';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { 
  AlertTriangle, Phone, MapPin, Plus, Trash2, User, Heart,
  Shield, Flame, Baby, Brain, Loader2, CheckCircle, X, Mic, MicOff,
  Volume2, Settings
} from 'lucide-react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';

const EmergencySOS = ({ user, setUser }) => {
  const [contacts, setContacts] = useState([]);
  const [emergencyNumbers, setEmergencyNumbers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sosActive, setSosActive] = useState(false);
  const [location, setLocation] = useState(null);
  const [showAddContact, setShowAddContact] = useState(false);
  const [newContact, setNewContact] = useState({ name: '', phone: '', relationship: '', is_primary: false });
  
  // Voice activation states
  const [voiceEnabled, setVoiceEnabled] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [voiceSupported, setVoiceSupported] = useState(false);
  const [lastHeard, setLastHeard] = useState('');
  const [showVoiceSettings, setShowVoiceSettings] = useState(false);
  
  const recognitionRef = useRef(null);
  const sosTriggeredRef = useRef(false);

  // Trigger words for SOS
  const TRIGGER_WORDS = ['sos', 'emergency', 'help', 'help me', 'call for help', 'mayday', 'ambulance'];

  useEffect(() => {
    loadData();
    getLocation();
    initVoiceRecognition();
    
    return () => {
      // Cleanup voice recognition on unmount
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, []);

  useEffect(() => {
    // Start/stop listening based on voiceEnabled
    if (voiceEnabled && voiceSupported && !sosActive) {
      startListening();
    } else {
      stopListening();
    }
  }, [voiceEnabled, voiceSupported, sosActive]);

  const initVoiceRecognition = () => {
    // Check for browser support
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    
    if (!SpeechRecognition) {
      process.env.NODE_ENV === 'development' && console.log('Speech recognition not supported');
      setVoiceSupported(false);
      return;
    }

    setVoiceSupported(true);
    
    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';

    recognition.onstart = () => {
      setIsListening(true);
      process.env.NODE_ENV === 'development' && console.log('Voice recognition started');
    };

    recognition.onend = () => {
      setIsListening(false);
      // Restart if voice is still enabled and SOS not active
      if (voiceEnabled && !sosActive && !sosTriggeredRef.current) {
        setTimeout(() => {
          try {
            recognition.start();
          } catch (e) {
            process.env.NODE_ENV === 'development' && console.log('Recognition restart error:', e);
          }
        }, 100);
      }
    };

    recognition.onerror = (event) => {
      process.env.NODE_ENV === 'development' && console.log('Voice recognition error:', event.error);
      if (event.error === 'not-allowed') {
        toast.error('Microphone access denied. Please enable microphone permissions.');
        setVoiceEnabled(false);
      }
    };

    recognition.onresult = (event) => {
      let finalTranscript = '';
      let interimTranscript = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcript;
        } else {
          interimTranscript += transcript;
        }
      }

      const heard = (finalTranscript || interimTranscript).toLowerCase().trim();
      setLastHeard(heard);

      // Check for trigger words
      if (!sosTriggeredRef.current && !sosActive) {
        for (const trigger of TRIGGER_WORDS) {
          if (heard.includes(trigger)) {
            process.env.NODE_ENV === 'development' && console.log(`Trigger word detected: "${trigger}" in "${heard}"`);
            sosTriggeredRef.current = true;
            triggerVoiceSOS();
            break;
          }
        }
      }
    };

    recognitionRef.current = recognition;
  };

  const startListening = () => {
    if (recognitionRef.current && !isListening) {
      try {
        sosTriggeredRef.current = false;
        recognitionRef.current.start();
        toast.success('Voice activation enabled. Say "SOS", "Emergency", or "Help"');
      } catch (e) {
        process.env.NODE_ENV === 'development' && console.log('Start listening error:', e);
      }
    }
  };

  const stopListening = () => {
    if (recognitionRef.current && isListening) {
      try {
        recognitionRef.current.stop();
      } catch (e) {
        process.env.NODE_ENV === 'development' && console.log('Stop listening error:', e);
      }
    }
  };

  const triggerVoiceSOS = useCallback(async () => {
    // Play alert sound
    try {
      const utterance = new SpeechSynthesisUtterance('SOS Alert Triggered. Notifying emergency contacts.');
      utterance.rate = 1.2;
      utterance.pitch = 1;
      window.speechSynthesis.speak(utterance);
    } catch (e) {
      process.env.NODE_ENV === 'development' && console.log('Speech synthesis error:', e);
    }

    toast.warning('🚨 Voice SOS Detected! Triggering alert...', { duration: 3000 });
    
    // Stop listening temporarily
    stopListening();
    
    // Trigger the actual SOS
    await triggerSOS();
  }, [location]);

  const loadData = async () => {
    try {
      const [contactsRes, numbersRes] = await Promise.all([
        axios.get(`${API}/emergency-sos/contacts`),
        axios.get(`${API}/emergency-sos/numbers`)
      ]);
      setContacts(contactsRes.data.contacts || []);
      setEmergencyNumbers(numbersRes.data.numbers || []);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          });
        },
        (error) => {
          console.error('Error getting location:', error);
          setLocation({ latitude: 28.6139, longitude: 77.2090 });
        }
      );
    }
  };

  const triggerSOS = async () => {
    if (!location) {
      toast.error('Unable to get your location');
      return;
    }

    setSosActive(true);
    
    try {
      const response = await axios.post(`${API}/emergency-sos/trigger`, {
        latitude: location.latitude,
        longitude: location.longitude,
        alert_type: 'sos'
      });
      
      toast.success('SOS Alert Sent! Help is on the way.');
      
      setTimeout(() => {
        toast.info('Call 112 for immediate emergency assistance');
      }, 2000);
      
    } catch (error) {
      toast.error('Failed to send SOS alert');
    }
  };

  const cancelSOS = () => {
    setSosActive(false);
    sosTriggeredRef.current = false;
    toast.info('SOS Alert Cancelled');
    
    // Resume listening if voice was enabled
    if (voiceEnabled) {
      setTimeout(() => {
        startListening();
      }, 1000);
    }
  };

  const addContact = async () => {
    if (!newContact.name || !newContact.phone) {
      toast.error('Please fill in name and phone number');
      return;
    }

    try {
      await axios.post(`${API}/emergency-sos/contacts`, newContact);
      toast.success('Emergency contact added');
      setShowAddContact(false);
      setNewContact({ name: '', phone: '', relationship: '', is_primary: false });
      loadData();
    } catch (error) {
      toast.error('Failed to add contact');
    }
  };

  const deleteContact = async (contactId) => {
    try {
      await axios.delete(`${API}/emergency-sos/contacts/${contactId}`);
      toast.success('Contact removed');
      loadData();
    } catch (error) {
      toast.error('Failed to remove contact');
    }
  };

  const callNumber = (number) => {
    window.location.href = `tel:${number}`;
  };

  const getIcon = (iconName) => {
    const icons = {
      phone: Phone,
      ambulance: Heart,
      shield: Shield,
      flame: Flame,
      heart: Heart,
      baby: Baby,
      user: User,
      brain: Brain
    };
    const Icon = icons[iconName] || Phone;
    return <Icon className="w-5 h-5" />;
  };

  const toggleVoiceActivation = () => {
    if (!voiceSupported) {
      toast.error('Voice recognition is not supported in your browser');
      return;
    }
    setVoiceEnabled(!voiceEnabled);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-orange-50">
        <Navigation user={user} setUser={setUser} />
        <div className="container mx-auto px-4 py-8 pt-24 flex justify-center">
          <Loader2 className="w-12 h-12 animate-spin text-red-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-orange-50">
      <Navigation user={user} setUser={setUser} />
      
      <div className="container mx-auto px-4 py-6 pt-24 max-w-4xl">
        {/* Voice Activation Banner */}
        {voiceSupported && (
          <Card className={`mb-4 ${voiceEnabled ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white' : 'bg-white'}`}>
            <CardContent className="py-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {isListening ? (
                    <div className="relative">
                      <Mic className="w-6 h-6 animate-pulse" />
                      <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full animate-ping" />
                    </div>
                  ) : (
                    <MicOff className="w-6 h-6" />
                  )}
                  <div>
                    <p className="font-medium">
                      {voiceEnabled ? 'Voice Activation ON' : 'Voice Activation OFF'}
                    </p>
                    <p className={`text-xs ${voiceEnabled ? 'text-white/80' : 'text-gray-500'}`}>
                      {voiceEnabled 
                        ? `Say "SOS", "Emergency", or "Help" to trigger alert`
                        : 'Enable to trigger SOS with your voice'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={voiceEnabled}
                    onCheckedChange={toggleVoiceActivation}
                    className={voiceEnabled ? 'data-[state=checked]:bg-white' : ''}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowVoiceSettings(true)}
                    className={voiceEnabled ? 'text-white hover:bg-white/20' : ''}
                  >
                    <Settings className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              {voiceEnabled && lastHeard && (
                <div className="mt-2 text-xs text-white/70">
                  Last heard: "{lastHeard}"
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Voice Settings Dialog */}
        <Dialog open={showVoiceSettings} onOpenChange={setShowVoiceSettings}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Mic className="w-5 h-5 text-green-600" />
                Voice Activation Settings
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <div className="p-4 bg-blue-50 rounded-lg">
                <h4 className="font-medium text-blue-800 mb-2">Trigger Words</h4>
                <p className="text-sm text-blue-700 mb-2">Say any of these to trigger SOS:</p>
                <div className="flex flex-wrap gap-2">
                  {TRIGGER_WORDS.map((word, i) => (
                    <Badge key={`item-${i}`} variant="secondary" className="bg-blue-100 text-blue-800">
                      "{word}"
                    </Badge>
                  ))}
                </div>
              </div>
              
              <div className="p-4 bg-yellow-50 rounded-lg">
                <h4 className="font-medium text-yellow-800 mb-2">Tips for Best Results</h4>
                <ul className="text-sm text-yellow-700 space-y-1">
                  <li>• Speak clearly and loudly</li>
                  <li>• Minimize background noise</li>
                  <li>• Keep device microphone unobstructed</li>
                  <li>• Works best in quiet environments</li>
                </ul>
              </div>

              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium text-gray-800 mb-2">Status</h4>
                <div className="flex items-center gap-2 text-sm">
                  <div className={`w-3 h-3 rounded-full ${isListening ? 'bg-green-500 animate-pulse' : 'bg-gray-300'}`} />
                  <span>{isListening ? 'Listening for trigger words...' : 'Not listening'}</span>
                </div>
              </div>

              <Button onClick={() => setShowVoiceSettings(false)} className="w-full">
                Close
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* SOS Button */}
        <Card className={`mb-8 ${sosActive ? 'bg-red-600 text-white' : 'bg-white'} transition-all`}>
          <CardContent className="p-8 text-center">
            {!sosActive ? (
              <>
                <button
                  onClick={triggerSOS}
                  className="w-40 h-40 rounded-full bg-gradient-to-br from-red-500 to-red-700 text-white flex flex-col items-center justify-center mx-auto shadow-2xl hover:scale-105 transition-transform active:scale-95 relative"
                  data-testid="sos-button"
                >
                  {isListening && (
                    <div className="absolute -top-2 -right-2 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center animate-pulse">
                      <Mic className="w-4 h-4 text-white" />
                    </div>
                  )}
                  <AlertTriangle className="w-16 h-16 mb-2" />
                  <span className="text-2xl font-bold">SOS</span>
                </button>
                <p className="mt-6 text-gray-600">
                  {voiceEnabled 
                    ? 'Press button or say "SOS" / "Emergency" / "Help"'
                    : 'Press and hold for emergency assistance'}
                </p>
                {location && (
                  <p className="mt-2 text-sm text-gray-500 flex items-center justify-center gap-1">
                    <MapPin className="w-4 h-4" />
                    Location acquired
                  </p>
                )}
              </>
            ) : (
              <>
                <div className="animate-pulse">
                  <AlertTriangle className="w-20 h-20 mx-auto mb-4" />
                  <h2 className="text-3xl font-bold mb-2">SOS ACTIVE</h2>
                  <p className="mb-4">Emergency contacts are being notified</p>
                </div>
                <Button 
                  onClick={cancelSOS} 
                  variant="outline" 
                  className="bg-white text-red-600 hover:bg-red-50"
                >
                  <X className="w-4 h-4 mr-2" />
                  Cancel SOS
                </Button>
              </>
            )}
          </CardContent>
        </Card>

        {/* Emergency Numbers */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Phone className="w-5 h-5 text-red-600" />
              Emergency Numbers
            </CardTitle>
            <CardDescription>Tap to call immediately</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {emergencyNumbers.map((num, index) => (
                <button
                  key={`item-${index}`}
                  onClick={() => callNumber(num.number)}
                  className="p-4 rounded-xl bg-gradient-to-br from-red-50 to-orange-50 border border-red-100 hover:border-red-300 transition-all text-left"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center text-red-600">
                      {getIcon(num.icon)}
                    </div>
                    <span className="font-bold text-xl text-red-600">{num.number}</span>
                  </div>
                  <p className="text-sm font-medium text-gray-800">{num.name}</p>
                  <p className="text-xs text-gray-500">{num.description}</p>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Emergency Contacts */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5 text-blue-600" />
                Emergency Contacts
              </CardTitle>
              <CardDescription>People to notify in emergencies</CardDescription>
            </div>
            <Dialog open={showAddContact} onOpenChange={setShowAddContact}>
              <DialogTrigger asChild>
                <Button size="sm" className="bg-blue-600 hover:bg-blue-700">
                  <Plus className="w-4 h-4 mr-1" />
                  Add Contact
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add Emergency Contact</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 pt-4">
                  <div>
                    <Label>Name</Label>
                    <Input
                      placeholder="Contact name"
                      value={newContact.name}
                      onChange={(e) => setNewContact({ ...newContact, name: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Phone Number</Label>
                    <Input
                      placeholder="+1 555 123 4567"
                      value={newContact.phone}
                      onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Relationship</Label>
                    <Input
                      placeholder="e.g., Spouse, Parent, Friend"
                      value={newContact.relationship}
                      onChange={(e) => setNewContact({ ...newContact, relationship: e.target.value })}
                    />
                  </div>
                  <Button onClick={addContact} className="w-full">
                    Add Contact
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </CardHeader>
          <CardContent>
            {contacts.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <User className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>No emergency contacts added yet</p>
                <p className="text-sm">Add contacts who should be notified in emergencies</p>
              </div>
            ) : (
              <div className="space-y-3">
                {contacts.map((contact) => (
                  <div
                    key={contact.id}
                    className="flex items-center justify-between p-4 rounded-lg bg-gray-50 border"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                        <User className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-medium">{contact.name}</p>
                        <p className="text-sm text-gray-500">{contact.phone}</p>
                      </div>
                      {contact.relationship && (
                        <Badge variant="outline">{contact.relationship}</Badge>
                      )}
                      {contact.is_primary && (
                        <Badge className="bg-green-100 text-green-800">Primary</Badge>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => callNumber(contact.phone)}
                      >
                        <Phone className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-600 hover:bg-red-50"
                        onClick={() => deleteContact(contact.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default EmergencySOS;
